import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle, ShieldCheck } from 'lucide-react';
import SiteLogo from '../components/Layout/SiteLogo';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/Layout/LanguageSwitcher';

type LoginData = { email: string; password: string };
type RegisterData = { name: string; email: string; password: string; confirmPassword: string };

const Login: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { login, verifyTwoFactor, register: registerUser } = useAuth();
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [twoFACode, setTwoFACode] = useState('');
  const notifications = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const loginSchema = useMemo(
    () =>
      yup.object({
        email: yup.string().required(t('login.validation.emailRequired')).email(t('login.validation.emailInvalid')),
        password: yup
          .string()
          .required(t('login.validation.passwordRequired'))
          .min(6, t('login.validation.passwordMin'))
      }),
    [t]
  );
  const registerSchema = useMemo(
    () =>
      yup.object({
        name: yup
          .string()
          .required(t('login.validation.nameRequired'))
          .min(2, t('login.validation.nameMin')),
        email: yup.string().required(t('login.validation.emailRequired')).email(t('login.validation.emailInvalid')),
        password: yup
          .string()
          .required(t('login.validation.passwordRequired'))
          .min(6, t('login.validation.passwordMin')),
        confirmPassword: yup
          .string()
          .required(t('login.validation.confirmRequired'))
          .oneOf([yup.ref('password')], t('login.validation.passwordMismatch'))
      }),
    [t]
  );
  const [canSelfRegister, setCanSelfRegister] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [inviteToken, setInviteToken] = useState<string | null>(null);

  const decodeInvitePayload = (token: string): { email?: string; name?: string } | null => {
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      );
      const payload = JSON.parse(json);
      return payload && typeof payload === 'object' ? payload : null;
    } catch {
      return null;
    }
  };

  const loginForm = useForm<LoginData>({
    resolver: yupResolver(loginSchema),
  });

  const registerForm = useForm<RegisterData>({
    resolver: yupResolver(registerSchema),
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const invite = params.get('invite');
    if (invite) {
      setInviteToken(invite);
      setIsLogin(false);
      const payload = decodeInvitePayload(invite);
      if (payload?.email) registerForm.setValue('email', String(payload.email), { shouldValidate: true });
      if (payload?.name) registerForm.setValue('name', String(payload.name), { shouldValidate: true });
    } else {
      setInviteToken(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const fetchRegistrationStatus = async () => {
      try {
        const response = await fetch('/api/auth/registration-status');
        if (!response.ok) {
          throw new Error('Erro ao verificar status de registro');
        }
        const data = await response.json();
        // Em produção, permitir auto-registro apenas se ainda não existir nenhum usuário
        // (mas convites sempre podem registrar)
        if (process.env.NODE_ENV === 'production') {
          setCanSelfRegister(!!inviteToken || !data.hasUsers);
        } else {
          setCanSelfRegister(true);
        }
      } catch (_) {
        // Em caso de erro, mantenha comportamento padrão (apenas login)
        setCanSelfRegister(!!inviteToken);
      } finally {
        setLoadingStatus(false);
      }
    };

    fetchRegistrationStatus();
  }, [inviteToken]);

  const onLoginSubmit = async (data: LoginData) => {
    try {
      setIsSubmitting(true);
      setError('');
      const result = await login(data.email, data.password);
      if (result.status === '2fa_required') {
        setPendingToken(result.pendingToken);
        setTwoFACode('');
      } else {
        notifications.success.login();
        navigate('/');
      }
    } catch (err: any) {
      setError(err.message);
      notifications.error.login(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onVerifyTwoFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingToken) return;
    try {
      setIsSubmitting(true);
      setError('');
      await verifyTwoFactor(pendingToken, twoFACode);
      notifications.success.login();
      navigate('/');
    } catch (err: any) {
      setError(err.message);
      notifications.error.login(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onRegisterSubmit = async (data: RegisterData) => {
    if (!canSelfRegister) {
      setError(t('login.regDisabled'));
      return;
    }
    try {
      setIsSubmitting(true);
      setError('');
      await registerUser(data.name, data.email, data.password, inviteToken || undefined);
      notifications.success.userCreated();
      navigate('/');
    } catch (err: any) {
      setError(err.message);
      notifications.error.userCreate(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-8 sm:py-12 sm:px-6 lg:px-8 relative">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <div className="w-full sm:mx-auto sm:w-full sm:max-w-md px-4">
        {/* Logo */}
        <div className="flex justify-center">
          <Link to="/">
            <SiteLogo size="lg" nameClassName="font-bold text-gray-900" />
          </Link>
        </div>

        <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-bold text-gray-900">
          {isLogin || !canSelfRegister ? t('login.titleLogin') : t('login.titleRegister')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {canSelfRegister && (
            <>
              {isLogin ? (
                <>
                  {t('login.or')}{' '}
                  <button
                    onClick={() => setIsLogin(false)}
                    className="font-medium text-primary-600 hover:text-primary-500"
                  >
                    {t('login.createAccount')}
                  </button>
                </>
              ) : (
                <>
                  {t('login.or')}{' '}
                  <button
                    onClick={() => setIsLogin(true)}
                    className="font-medium text-primary-600 hover:text-primary-500"
                  >
                    {t('login.existingAccount')}
                  </button>
                </>
              )}
            </>
          )}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-6 sm:py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* 2FA Verification Step */}
          {pendingToken ? (
            <form onSubmit={onVerifyTwoFactor} className="space-y-6">
              <div className="flex items-center gap-2 text-gray-700">
                <ShieldCheck className="w-5 h-5 text-primary-600" />
                <p className="text-sm">Digite o código do seu app autenticador, ou um código de recuperação.</p>
              </div>
              <div>
                <label htmlFor="twoFACode" className="block text-sm font-medium text-gray-700">
                  Código de verificação
                </label>
                <input
                  id="twoFACode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value)}
                  className="input-field mt-1 text-center text-lg tracking-widest"
                  placeholder="000000"
                />
              </div>
              <div>
                <button
                  type="submit"
                  disabled={isSubmitting || !twoFACode}
                  className="btn-primary w-full flex justify-center py-3 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Verificando...</span>
                    </div>
                  ) : (
                    'Verificar'
                  )}
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPendingToken(null);
                  setTwoFACode('');
                  setError('');
                }}
                className="w-full text-sm text-gray-600 hover:text-primary-500 transition-colors duration-200"
              >
                ← Voltar ao login
              </button>
            </form>
          ) : isLogin || !canSelfRegister ? (
            <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="mt-1 relative">
                  <input
                    {...loginForm.register('email')}
                    type="email"
                    autoComplete="email"
                    className="input-field pl-10"
                    placeholder="seu@email.com"
                  />
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>
                {loginForm.formState.errors.email && (
                  <p className="mt-1 text-sm text-red-600">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Senha
                </label>
                <div className="mt-1 relative">
                  <input
                    {...loginForm.register('password')}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="input-field pl-10 pr-10"
                    placeholder="Sua senha"
                  />
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="mt-1 text-sm text-red-600">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Lembrar de mim
                  </label>
                </div>

              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-primary-600 hover:text-primary-500">
                  Esqueceu a senha?
                </Link>
              </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex justify-center py-3 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Entrando...</span>
                    </div>
                  ) : (
                    'Entrar'
                  )}
                </button>
              </div>
            </form>
          ) : (
            /* Register Form */
            <form onSubmit={registerForm.handleSubmit(onRegisterSubmit)} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Nome Completo
                </label>
                <div className="mt-1">
                  <input
                    {...registerForm.register('name')}
                    type="text"
                    autoComplete="name"
                    className="input-field"
                    placeholder="Seu nome completo"
                  />
                </div>
                {registerForm.formState.errors.name && (
                  <p className="mt-1 text-sm text-red-600">{registerForm.formState.errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="mt-1 relative">
                  <input
                    {...registerForm.register('email')}
                    type="email"
                    autoComplete="email"
                    className="input-field pl-10"
                    placeholder="seu@email.com"
                  />
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>
                {registerForm.formState.errors.email && (
                  <p className="mt-1 text-sm text-red-600">{registerForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Senha
                </label>
                <div className="mt-1 relative">
                  <input
                    {...registerForm.register('password')}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="input-field pl-10 pr-10"
                    placeholder="Mínimo 6 caracteres"
                  />
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {registerForm.formState.errors.password && (
                  <p className="mt-1 text-sm text-red-600">{registerForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirmar Senha
                </label>
                <div className="mt-1 relative">
                  <input
                    {...registerForm.register('confirmPassword')}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="input-field pl-10"
                    placeholder="Confirme sua senha"
                  />
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>
                {registerForm.formState.errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{registerForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  required
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
                  Aceito os{' '}
                  <button type="button" className="text-primary-600 hover:text-primary-500">
                    termos de uso
                  </button>{' '}
                  e{' '}
                  <button type="button" className="text-primary-600 hover:text-primary-500">
                    política de privacidade
                  </button>
                </label>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex justify-center py-3 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Criando conta...</span>
                    </div>
                  ) : (
                    'Criar Conta'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* First User Notice (apenas enquanto não existe super admin) */}
          {!isLogin && canSelfRegister && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Primeiro usuário?</p>
                  <p>O primeiro usuário registrado será automaticamente configurado como super administrador.</p>
                </div>
              </div>
            </div>
          )}

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <Link
              to="/"
              className="text-sm text-gray-600 hover:text-primary-500 transition-colors duration-200"
            >
              ← Voltar ao início
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
